import React, { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { EditorState } from 'draft-js';
import cn from 'classnames';
import pick from 'lodash/pick';
import { isMobile } from 'react-device-detect';
import identity from 'lodash/identity';

import { getMeetingItemByUuid } from 'Shared/selectors/common';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import {
  modifyUrlBySmartLink,
  applySectionHeaderStyling,
} from 'Shared/components/DraftEditor/plugins/smartLinksPlugin';
import { removeTodoNesting, updateItem, handleEmptyItemRemove } from 'Shared/actions/todos';
import { editorStateToJsonString, editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';
import { ItemType, WorkspaceItem } from 'Shared/constants/types';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import {
  PLACEHOLDER_FOR_ACTION_ITEM,
  PLACEHOLDER_FOR_DISCUSSION_TOPIC,
  PLACEHOLDER_FOR_SECTION_HEADER,
} from 'Shared/constants/constants';

import MeetingItemIndicators from 'Shared/components/MeetingItemsContainer/MeetingItemIndicators';

import { getOffsetFromSelection } from './utils';

import './ItemEditorWrapper.scss';

import '@workpatterns/draft-js-mention-plugin/lib/plugin.css';
import 'draft-js/dist/Draft.css';

const neededItemFields = Object.freeze([
  'id',
  'itemType',
  'body',
  'uuid',
  'parentUuid',
  'isSection',
  'video',
  'workspaceId',
  'parentId',
  'position',
  'completed',
  'goalSlug',
]);

type Props = {
  isDisabled: boolean;
  uuid: string;
  editorRef: {
    current: HTMLDivElement;
  };
  containerRef: {
    current: HTMLDivElement;
  };
  isShowLoader: boolean;
  hideActionsAreaOnDesktop: boolean;
  isFocused: boolean;
  onAddItem: Function;
  nestItemByTabClick: Function;
  onArrowUpClick: Function;
  onArrowDownClick: Function;
  fileUploadProccess: Function;
};

function ItemEditorWrapper({
  isDisabled = false,
  uuid: itemUuid,
  isShowLoader,
  editorRef,
  containerRef,
  isFocused,
  onAddItem = identity,
  nestItemByTabClick,
  onArrowUpClick,
  onArrowDownClick,
  fileUploadProccess,
  hideActionsAreaOnDesktop,
}: Props) {
  const dispatch = useDispatch();
  const todoData: WorkspaceItem = useDeepEqualSelector(state =>
    pick(getMeetingItemByUuid(itemUuid)(state), neededItemFields)
  );

  const { id, itemType, body, uuid, parentUuid, video, goalSlug, isSection, completed } = todoData;
  const isSectionHeader = itemType === ItemType.discussionTopic && isSection;
  const [editorState, setEditorState] = useState(editorStateForEditorComponent(body));
  const [readOnly, setReadonly] = useState(true);
  const focusTimeoutId = useRef<number>(null);
  const focusOnMounTimeoutId = useRef<number>(null);
  const saveTimeoutId = useRef<number>(null);

  const idRef = useRef(id);
  useEffect(() => {
    idRef.current = id;
  }, [id]);

  const isInViewport = useCallback(() => {
    if (!containerRef.current) return false;
    const bounding = containerRef.current.getBoundingClientRect();
    return (
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, [containerRef]);

  const scrollIntoView = useCallback(() => {
    if (containerRef?.current && !isInViewport())
      containerRef.current.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [containerRef, isInViewport]);

  const save = useCallback(
    newEditorState => {
      const editorStateJson = editorStateToJsonString(newEditorState);
      if (saveTimeoutId.current) {
        clearTimeout(saveTimeoutId.current);
      }

      // Only save if we have an ID, because then we know the item has been persisted to the DB
      // this helps avoid timing issues where we try to update an item that hasn't persisted yet.
      // If no ID exists, then wait 500ms and try to save again.  Timeout will get cleared every
      // time save gets called.
      if (idRef.current) {
        dispatch(updateItem(uuid, { body: editorStateJson, itemType }));
      } else {
        saveTimeoutId.current = window.setTimeout(() => {
          save(newEditorState);
        }, 500);
      }
    },
    [uuid, itemType, dispatch]
  );

  const updateInRedux = useCallback(
    ({ currentBody, newEditorState }) => {
      const editorStateJson = editorStateToJsonString(newEditorState);
      if (currentBody === editorStateJson) return;

      dispatch({
        type: 'MEETING_ITEM_UPDATE_REQUEST',
        meetingItemType: itemType,
        payload: { uuid, body: editorStateJson },
      });
      save(newEditorState);
    },
    [uuid, itemType, dispatch, save]
  );

  const debounceUpdateInRedux = useCallback(
    debounce(params => updateInRedux(params), 500),
    [updateInRedux]
  );

  useEffect(() => {
    if (isFocused) scrollIntoView();
    return () => {
      if (focusTimeoutId.current) clearTimeout(focusTimeoutId.current);
      if (focusOnMounTimeoutId.current) clearTimeout(focusOnMounTimeoutId.current);
      debounceUpdateInRedux.cancel();
    };
  }, []);

  const handleFocusOnMount = () => {
    if (editorRef?.current) {
      if (readOnly && !isDisabled) setReadonly(false);
      if (!editorState.getSelection().getHasFocus()) editorRef.current.focus();
    } else focusTimeoutId.current = window.setTimeout(() => handleFocusOnMount(), 1000);
  };

  useEffect(() => {
    if (isFocused) {
      if (containerRef.current?.scrollIntoView)
        containerRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      // Immediate focus on mounted component breaks initialization of draftjs plugins, need to use setTimeout
      focusOnMounTimeoutId.current = window.setTimeout(() => handleFocusOnMount(), 0);
    }
  }, [isFocused]);

  useEffect(() => {
    const hasFocus = editorState.getSelection().getHasFocus();
    if (!hasFocus && editorStateToJsonString(editorState) !== body) setEditorState(editorStateForEditorComponent(body));
  }, [body]);

  const editorPlaceholder = useMemo(() => {
    if (isSectionHeader) return PLACEHOLDER_FOR_SECTION_HEADER;
    if (itemType === ItemType.actionItem) return PLACEHOLDER_FOR_ACTION_ITEM;
    return PLACEHOLDER_FOR_DISCUSSION_TOPIC;
  }, [itemType, isSectionHeader]);

  const handleEditorContainerClick = () => {
    if (isDisabled) return null;

    if (editorRef?.current) editorRef.current.focus();

    if (readOnly) setReadonly(false);

    // At the first click we allow editing of text and we set focus on a place of a click
    if (readOnly && editorState.getCurrentContent().hasText()) {
      const offset = getOffsetFromSelection(editorState);
      const selection = editorState.getSelection();
      setEditorState(
        EditorState.forceSelection(
          editorState,
          selection.merge({
            focusOffset: offset,
            anchorOffset: offset,
          })
        )
      );
    }
    return null;
  };

  const setState = useCallback(
    state => {
      if (typeof state === 'function') {
        setEditorState(state({ editorState }).editorState);
      } else {
        setEditorState(state.editorState);
      }
    },
    [editorState]
  );

  const handleEditorChange = useCallback(
    newEditorState => modifyUrlBySmartLink(applySectionHeaderStyling(newEditorState, isSectionHeader), { setState }),
    [isSectionHeader, setState]
  );

  useEffect(() => {
    debounceUpdateInRedux({ currentBody: body, newEditorState: editorState });
  }, [editorState]);

  const handleEditorContainerKeyPress = evt => {
    if (evt.key === 'Tab') {
      evt.preventDefault();
      if (evt.shiftKey && parentUuid) dispatch(removeTodoNesting({ uuid, itemType, goalSlug, completed }));
      else if (!evt.shiftKey && nestItemByTabClick) nestItemByTabClick(uuid);
    } else if (evt.key === 'Backspace' && !editorState.getCurrentContent().hasText()) {
      evt.preventDefault();
      const removed = dispatch(handleEmptyItemRemove(uuid));

      if (removed) {
        onArrowUpClick({ uuid });
        debounceUpdateInRedux.cancel();
      }
    } else if (evt.key === 'ArrowUp' && evt.keyCode === 38) onArrowUpClick({ uuid });
    else if (evt.key === 'ArrowDown' && evt.keyCode === 40) onArrowDownClick({ uuid });
  };

  const handleKeyCommandActionsMap = useCallback(
    () => ({
      'save-block': () => {
        // Don't create another item if enter is pressed on a blank item
        if (editorState.getCurrentContent().hasText()) {
          onAddItem(uuid);
        }
      },
    }),
    [onAddItem, uuid, editorState]
  );

  // Disable editing when focus lost
  const handleBlur = () => {
    setTimeout(() => setReadonly(true));
    debounceUpdateInRedux({ currentBody: body, newEditorState: editorState });
  };

  const handlePastedFiles = useCallback(
    files => {
      const file = files[0];
      if (!file) return;
      fileUploadProccess(file);
    },
    [fileUploadProccess]
  );

  const hasUploadingVideo = video && !video.id;

  return (
    <div
      className={cn('item-editor-wrapper', {
        'item-editor-wrapper--mobile': isMobile && readOnly,
      })}
      onClick={handleEditorContainerClick}
      onKeyDown={handleEditorContainerKeyPress}
      onBlur={handleBlur}
    >
      <DraftEditor
        meetingItemUuid={itemUuid}
        editorState={editorState}
        placeholder={editorPlaceholder}
        editorRef={editorRef}
        onChange={handleEditorChange}
        handlePastedFiles={handlePastedFiles}
        onKeyCommandActionsMap={handleKeyCommandActionsMap}
        readOnly={readOnly || hasUploadingVideo || isShowLoader}
      />
      {!hideActionsAreaOnDesktop && (
        <div className="item-editor-wrapper-indicators">
          <MeetingItemIndicators uuid={uuid} />
        </div>
      )}
    </div>
  );
}

export default memo(ItemEditorWrapper);
